<script setup lang="ts">
// No-image typographic cover tile for informational (third-party) events.
// Renders a deterministic HSL gradient keyed off the event id so the same
// event always shows the same backdrop (SSR/client stable, no flicker) and
// distinct events get visibly different hues. SSR-safe: `hueFromId` is a pure
// auto-imported util and no browser APIs are touched during setup.

const props = defineProps<{
  id: string;
  title: string;
  eventKind: string;
  city?: string | null;
  state?: string | null;
}>();

const kindMeta = computed(() => informationalEventKindMeta(props.eventKind));

// Two related HSL stops derived from the deterministic hue: the second stop is
// nudged ~25deg around the wheel and shaded slightly darker for depth. Tuned
// saturation/lightness keep the backdrop rich but legible under light text.
const coverStyle = computed(() => {
  const baseHue = hueFromId(props.id);
  const accentHue = (baseHue + 25) % 360;
  const start = `hsl(${baseHue}, 58%, 42%)`;
  const end = `hsl(${accentHue}, 62%, 28%)`;
  return {
    background: `linear-gradient(135deg, ${start}, ${end})`,
  };
});

const locationLine = computed(() => {
  const parts = [props.city, props.state].filter(
    (part): part is string => !!part,
  );
  return parts.join(', ');
});
</script>

<template>
  <v-responsive
    :aspect-ratio="16 / 9"
    class="informational-cover"
    :style="coverStyle"
  >
    <div class="informational-cover__icon">
      <v-icon :icon="kindMeta.icon" size="88" />
    </div>

    <div class="informational-cover__overlay">
      <div class="informational-cover__title text-subtitle-1 font-weight-bold">
        {{ title }}
      </div>
      <div
        v-if="locationLine"
        class="informational-cover__location text-caption"
      >
        {{ locationLine }}
      </div>
    </div>
  </v-responsive>
</template>

<style scoped>
.informational-cover {
  position: relative;
  overflow: hidden;
}

.informational-cover__icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.28);
}

.informational-cover__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0.75rem 1rem;
  /* Subtle dark scrim at the base for text contrast over any hue. */
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.45),
    rgba(0, 0, 0, 0) 60%
  );
}

.informational-cover__title {
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.25;
}

.informational-cover__location {
  color: rgba(255, 255, 255, 0.88);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.55);
  margin-top: 0.125rem;
}
</style>
